"use client";
import { useEffect, useState } from "react";
import { type Step } from "./surveySteps";
import { Option } from "./surveySteps";
import { Check, ChevronLeft, ChevronRight } from "lucide-react";
import {
  getSurveySteps,
  postSurveyFill,
} from "../services/survey/surveyService";
import { useNavigate } from "react-router-dom";
import { dictionary } from "../utils/dictionary";
import { Language } from "../utils/dictionary";
import { useAuth } from "../context/AuthContext";

export default function InitialSurvey() {
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [currentStepId, setCurrentStepId] = useState<string>("");
  const [history, setHistory] = useState<string[]>([]);
  const [surveySteps, setSurveySteps] = useState<Step[]>([]);
  const [currentStep, setCurrentStep] = useState<Step | undefined>(undefined);
  const { language, country } = useAuth();


  useEffect(() => {
    getSurveySteps("default").then((steps) => {
      setSurveySteps(steps.data as Step[]);
      setCurrentStepId(steps.data[0].id);
    });
  }, []);

  useEffect(() => {
    if (surveySteps.length > 0 && currentStepId) {
      setCurrentStep(
        surveySteps.find((s: Step) => s.id === currentStepId) as
          | Step
          | undefined
      );
    }
  }, [surveySteps, currentStepId]);

  if (!currentStep) return null;

  const handleAnswer = async (value: any) => {
    await new Promise((resolve) => setTimeout(resolve, 500));

    const updated = { ...answers, [currentStep.id]: value };
    setAnswers(updated);

    let nextId: string | undefined;

    if (typeof value === "object" && value?.next) {
      nextId = value.next;
    } else if (
      typeof currentStep.next_step === "string" &&
      currentStep.next_step
    ) {
      nextId = currentStep.next_step;
    }

    if (currentStep.options) {
      const selectedOpt = currentStep.options.find(
        (opt) => opt.value === value
      );
      if (selectedOpt?.next) nextId = selectedOpt.next;
    }

    if (nextId === "summary") {
      handleSubmit(updated);
    }

    if (nextId) {
      setHistory((prev) => [...prev, currentStep.id]);
      setCurrentStepId(nextId);
    } else {
      handleSubmit(updated);
    }
  };

  const handleSubmit = async (data: Record<string, any>) => {
    const payload = {
      entity_type: "user",
      entity_id: localStorage.getItem("user_id") || "",
      survey_id: "initial",
      answers: data,
    };
    payload.answers.summary = true;
    await postSurveyFill(payload, "default")
      .then((res: any) => {
        console.log(res);
      })
      .catch((err: any) => {
        console.error(err);
      });
  };

  const handleBack = () => {
    setHistory((prev) => {
      if (prev.length === 0) return prev;
      const newHistory = [...prev];
      const prevStepId = newHistory.pop();
      if (prevStepId) setCurrentStepId(prevStepId);
      return newHistory;
    });
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-[#F5F6FA] font-['Plus_Jakarta_Sans',sans-serif] overflow-y-auto">
      {/* Background Gradient - Fixed to cover entire viewport */}
      <div
        className="fixed inset-0 z-0 pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(62, 62, 255, 0) 0%, rgba(62, 110, 255, 0.2) 100%), linear-gradient(90deg, rgb(255, 255, 255) 0%, rgb(255, 255, 255) 100%)",
        }}
      />

      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center container mx-auto px-4 sm:px-6 md:px-8 lg:px-12 py-6 sm:py-8 md:py-10 lg:py-12">
        <div className="w-full max-w-2xl mx-auto p-4 sm:p-6 md:p-8 rounded-xl sm:rounded-2xl text-center">
          {currentStep.type !== "summary" && (
            <h1 className="font-bold text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-[58px] leading-[1.1] mb-4 sm:mb-6 md:mb-8 text-center sm:text-start">
              {dictionary[currentStep.question_text][language as Language] ||
                currentStep.question_text}
            </h1>
          )}
          {currentStep.description && (
            <p className="text-sm sm:text-base text-gray-500 mb-4 sm:mb-6 text-center sm:text-start">
              {dictionary[currentStep.description][language as Language] ||
                currentStep.description}
            </p>
          )}

          {currentStep.type === "single" && currentStep.options && (
            <div className="flex flex-col gap-3">
              {currentStep.options.map((opt, index) => (
                <ButtonAnswer
                  key={index.toString()}
                  onClick={() => handleAnswer(opt.value)}
                  option={opt}
                  language={language}
                  country={country || ""}
                  currentStepId={currentStepId}
                />
              ))}
            </div>
          )}

          {currentStep.type === "multi" && currentStep.options && (
            <MultiSelectStep
              options={currentStep.options}
              onSubmit={(values) => handleAnswer(values)}
              language={language}
            />
          )}

          {currentStep.type === "textarea" && (
            <TextAreaStep
              language={language}
              key={currentStep.id.toString()}
              onSubmit={(value) => handleAnswer(value)}
            />
          )}

          {currentStep.type === "summary" && (
            <SummaryStep language={language} />
          )}

          {currentStep.type !== "summary" && history.length > 0 && (
            <div
              onClick={handleBack}
              className="mt-4 sm:mt-6 flex items-center justify-center sm:justify-start gap-2 cursor-pointer hover:text-black transition-all"
            >
              <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500" />
              <p className="text-sm sm:text-base md:text-lg font-medium text-gray-500">
                {dictionary["back"][language as Language] || "Volver"}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const ButtonAnswer = ({
  key,
  onClick,
  option,
  language,
  country,
  currentStepId,
}: {
  key: string;
  onClick: () => void;
  option: Option;
  language: Language;
  country: string;
  currentStepId: string;
}) => {
  const [selected, setSelected] = useState<any>(null);
  const handleClick = () => {
    setSelected({ value: option.value, key: option.next });
    onClick();
  };

  return (
    <button
      key={key}
      onClick={handleClick}
      className={`
                w-full
                text-base 
                sm:text-lg 
                md:text-xl 
                lg:text-2xl 
                py-3 
                sm:py-4 
                md:py-6 
                lg:py-8 
                px-4 
                sm:px-5 
                md:px-6 
                font-medium 
                hover:shadow-md 
                active:scale-95
                text-black 
                rounded-xl 
                border 
                border-gray-200 
                transition-all 
                duration-200
                shadow-sm 
                ${
                  selected?.value === option.value &&
                  selected?.key === option.next
                    ? "bg-[#0E172A] text-white"
                    : "bg-white"
                }
            `}
    >
      {country !== "CO" && currentStepId === "monthlySales"
        ? dictionary[option.label + "-USD"][language as Language] ||
          option.label
        : dictionary[option.label][language as Language] || option.label}
    </button>
  );
};

interface MultiSelectStepProps {
  options: Step["options"];
  onSubmit: (values: string[]) => void;
  language: Language;
}

function MultiSelectStep({
  options = [],
  onSubmit,
  language,
}: MultiSelectStepProps) {
  const [selected, setSelected] = useState<string[]>([]);
  const toggleOption = (value: string) => {
    setSelected((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  return (
    <div className="flex flex-col gap-2 sm:gap-3">
      {options.map((opt) => (
        <button
          key={opt.value.toString()}
          onClick={() => toggleOption(opt.value.toString())}
          className={`w-full border py-3 sm:py-4 md:py-6 lg:py-8 px-4 sm:px-5 md:px-6 rounded-xl transition-all duration-200 active:scale-95 text-start text-base sm:text-lg md:text-xl lg:text-2xl font-bold hover:shadow-md ${
            selected.includes(opt.value.toString())
              ? "bg-[#0E172A] text-white"
              : "bg-white text-gray-700 border-gray-300"
          }`}
        >
          {dictionary[opt.label][language as Language] || opt.label}
        </button>
      ))}
      <ButtonContinue
        onClick={() => onSubmit(selected)}
        disabled={selected.length === 0}
        text={dictionary["continue"][language as Language] || "Continuar"}
      />
    </div>
  );
}

interface TextAreaStepProps {
  onSubmit: (value: string) => void;
  language: Language;
}

function TextAreaStep({ onSubmit, language }: TextAreaStepProps) {
  const [value, setValue] = useState("");
  return (
    <div className="flex flex-col gap-3 sm:gap-4">
      <textarea
        className="border rounded-lg p-3 sm:p-4 text-sm sm:text-base w-full h-[150px] sm:h-[180px] md:h-[200px] resize-none focus:outline-none focus:ring-2 focus:ring-[#0E172A]"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={
          dictionary["write-your-answer-here"][language as Language] ||
          "Escribe tu respuesta aquí..."
        }
      />
      <ButtonContinue
        onClick={() => onSubmit(value)}
        disabled={!value.trim()}
        text={dictionary["continue"][language as Language] || "Continuar"}
      />
    </div>
  );
}

function SummaryStep({
  language,
}: {
  language: Language;
}) {
  const navigate = useNavigate();

  return (
    <div className="flex flex-col items-center justify-center">
      <div className="flex items-center justify-center bg-[#0E172A] rounded-full p-3 sm:p-4 mb-4 sm:mb-6">
        <Check className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 text-white" />
      </div>
      <h1 className="font-bold text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-[58px] leading-[1.1] mb-6 sm:mb-8 text-[#0E172A] text-center">
        {dictionary["all-set"][language as Language] || "Todo listo"}
      </h1>
      <ButtonContinue
        onClick={() => {
          if (window.innerWidth < 768) {
            navigate("/settings");
          } else {
            navigate("/home");
          }
        }}
        disabled={false}
        text={
          dictionary["go-to-account"][language as Language] || "Ir a mi cuenta"
        }
      />
    </div>
  );
}

const ButtonContinue = ({
  onClick,
  disabled,
  text = "Continuar",
}: {
  onClick: () => void;
  disabled: boolean;
  text?: string;
}) => {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="flex items-center justify-center gap-2 mt-4 sm:mt-6 bg-[#0E172A] hover:bg-[#0E172A]/80 active:bg-[#0E172A]/70 text-base sm:text-lg md:text-xl lg:text-2xl font-bold text-white py-3 sm:py-4 px-5 sm:px-6 w-full sm:w-auto rounded-xl disabled:opacity-40 transition-all duration-300 hover:scale-105 active:scale-95"
    >
      <span>{text}</span>
      <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 lg:w-8 lg:h-8 text-white flex-shrink-0" />
    </button>
  );
};
